import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../constants/urlConstants";
import { RootState } from "../../store";

/**
* 🧩 Type Definitions
*/
export interface SliderItem {
id: number;
name: string;
url: string;
}

export interface Brand {
id: number;
name: string;
image: string;
}

export interface PerfumeItem {
id: number;
name: string;
price: number;
image: string;
}

export interface HomeResponse {
sliderItems: SliderItem[];
brands: Brand[];
perfumes: PerfumeItem[];
categories?: string[]; // Optional, can be added later
}

export interface HomeState {
data: HomeResponse | null;
loading: boolean;
error: string | null;
}

/**
* 🌀 Initial State
*/
const initialState: HomeState = {
data: null,
loading: false,
error: null,
};

/**
* 🚀 Async Thunk — Fetch Home Page Data
*/
export const fetchHomeData = createAsyncThunk<HomeResponse>(
"home/fetchHomeData",
async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<HomeResponse>(`${BASE_URL}/api/v1/home`);
      return response.data;
    } catch (error: any) {
      console.error("Failed to fetch home data:", error);
      return rejectWithValue(error.response?.data?.message || "Failed to load home data");
    }
  }
);

/**
 * 🧠 Slice
 */
const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    resetHomeState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

/**
 * 🧾 Export Reducer & Actions
 */
export const { resetHomeState } = homeSlice.actions;
export default homeSlice.reducer;

/**
 * 📦 Selectors
 */
export const selectHomeData = (state: RootState) => state.home.data;
export const selectHomeLoading = (state: RootState) => state.home.loading;
export const selectHomeError = (state: RootState) => state.home.error;
